import React, { useEffect, useState } from 'react';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Text,
  BlockStack,
  Spinner,
  Button,
  Modal,
  Select,
  Banner,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';

interface ShopStats {
  totalReviews: number;
  pendingReviews: number;
  approvedReviews: number;
  rejectedReviews: number;
  averageRating: number;
  productsWithReviews: number;
}

interface Shop {
  id: string;
  shop_domain: string;
  subscription_plan: 'free' | 'paid';
  subscription_status: 'free' | 'active' | 'cancelled' | 'pending';
  subscription_created_at: string | null;
  locale: string;
  created_at: string;
  updated_at: string;
  stats: ShopStats;
}

const AdminShops: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [shops, setShops] = useState<Shop[]>([]);
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [plan, setPlan] = useState<'free' | 'paid'>('free');
  const [status, setStatus] = useState<'free' | 'active' | 'cancelled' | 'pending'>('free');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchShops();
  }, [navigate]);

  const fetchShops = async () => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      const token = localStorage.getItem('admin_token');

      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/admin-shops`, {
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'X-Admin-Token': token,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setShops(data.shops || []);
      } else if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getSubscriptionBadge = (plan: string, status: string): React.ReactNode => {
    if (plan === 'paid' && status === 'active') {
      return <Badge tone="success">PRO Ativo</Badge>;
    }
    if (plan === 'paid' && status === 'cancelled') {
      return <Badge tone="critical">PRO Cancelado</Badge>;
    }
    if (plan === 'paid' && status === 'pending') {
      return <Badge tone="attention">PRO Pendente</Badge>;
    }
    return <Badge>FREE</Badge>;
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const handleManagePlan = (shop: Shop) => {
    setSelectedShop(shop);
    setPlan(shop.subscription_plan);
    setStatus(shop.subscription_status);
    setShowPlanModal(true);
    setMessage(null);
  };

  const handleSavePlan = async () => {
    if (!selectedShop) return;

    setSaving(true);
    setMessage(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      const token = localStorage.getItem('admin_token');

      if (!token) {
        navigate('/admin/login');
        return;
      }

      // Se mudando para free, status deve ser free
      const finalStatus = plan === 'free' ? 'free' : status;

      const response = await fetch(`${supabaseUrl}/functions/v1/admin-plans`, {
        method: 'POST',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'X-Admin-Token': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          shopId: selectedShop.id,
          plan,
          status: finalStatus,
        }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Plano atualizado com sucesso!' });
        await fetchShops();
        setTimeout(() => {
          setShowPlanModal(false);
          setSelectedShop(null);
          setMessage(null);
        }, 1500);
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.error || 'Erro ao atualizar plano' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao atualizar plano. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  const rows = shops.map((shop) => [
    shop.shop_domain,
    getSubscriptionBadge(shop.subscription_plan, shop.subscription_status),
    shop.stats.totalReviews.toString(),
    shop.stats.approvedReviews.toString(),
    shop.stats.pendingReviews.toString(),
    shop.stats.averageRating > 0 ? shop.stats.averageRating.toFixed(1) : '-',
    shop.stats.productsWithReviews.toString(),
    formatDate(shop.created_at),
    <Button key={shop.id} size="micro" onClick={() => handleManagePlan(shop)}>
      Gerenciar Plano
    </Button>,
  ]);

  return (
    <Page
      title="Lojas Cadastradas"
      primaryAction={{
        content: 'Voltar ao Dashboard',
        onAction: () => navigate('/admin/dashboard'),
      }}
      secondaryActions={[
        {
          content: 'Atualizar',
          onAction: fetchShops,
        },
        {
          content: 'Sair',
          onAction: () => {
            localStorage.removeItem('admin_token');
            navigate('/admin/login');
          },
        },
      ]}
    >
      <BlockStack gap="500">
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Total: {shops.length} {shops.length === 1 ? 'loja' : 'lojas'}
            </Text>
            
            <DataTable
              columnContentTypes={[
                'text',
                'text',
                'numeric',
                'numeric',
                'numeric',
                'numeric',
                'numeric',
                'text',
                'text',
              ]}
              headings={[
                'Domínio da Loja',
                'Plano',
                'Total Reviews',
                'Aprovadas',
                'Pendentes',
                'Média Rating',
                'Produtos',
                'Data Cadastro',
                'Ações',
              ]}
              rows={rows}
            />
          </BlockStack>
        </Card>

        {shops.length === 0 && (
          <Card>
            <BlockStack gap="300" align="center">
              <Text as="p" tone="subdued">
                Nenhuma loja cadastrada ainda.
              </Text>
            </BlockStack>
          </Card>
        )}
      </BlockStack>

      {/* Modal para gerenciar plano */}
      <Modal
        open={showPlanModal}
        onClose={() => {
          setShowPlanModal(false);
          setSelectedShop(null);
          setMessage(null);
        }}
        title="Gerenciar Plano"
        primaryAction={{
          content: 'Salvar',
          onAction: handleSavePlan,
          loading: saving,
        }}
        secondaryActions={[
          {
            content: 'Cancelar',
            onAction: () => {
              setShowPlanModal(false);
              setSelectedShop(null);
              setMessage(null);
            },
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {message && (
              <Banner
                tone={message.type === 'success' ? 'success' : 'critical'}
                onDismiss={() => setMessage(null)}
              >
                {message.text}
              </Banner>
            )}

            {selectedShop && (
              <>
                <BlockStack gap="200">
                  <Text as="p" variant="headingSm">
                    Loja: {selectedShop.shop_domain}
                  </Text>
                  <Text as="p" tone="subdued" variant="bodySm">
                    Plano atual: {getSubscriptionBadge(selectedShop.subscription_plan, selectedShop.subscription_status)}
                  </Text>
                </BlockStack>

                <Select
                  label="Plano"
                  options={[
                    { label: 'FREE', value: 'free' },
                    { label: 'PRO', value: 'paid' },
                  ]}
                  value={plan}
                  onChange={(value) => {
                    setPlan(value as 'free' | 'paid');
                    if (value === 'free') {
                      setStatus('free');
                    } else if (status === 'free') {
                      setStatus('active');
                    }
                  }}
                />

                {plan === 'paid' && (
                  <Select
                    label="Status do Plano PRO"
                    options={[
                      { label: 'Ativo', value: 'active' },
                      { label: 'Pendente', value: 'pending' },
                      { label: 'Cancelado', value: 'cancelled' },
                    ]}
                    value={status}
                    onChange={(value) => setStatus(value as 'active' | 'pending' | 'cancelled')}
                    helpText="Status da assinatura PRO. 'Ativo' permite acesso completo aos recursos PRO."
                  />
                )}

                {plan === 'free' && (
                  <Text as="p" tone="subdued" variant="bodySm">
                    Ao mudar para FREE, o status será automaticamente definido como 'free'.
                  </Text>
                )}
              </>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default AdminShops;

